import React, { useEffect } from 'react';
import FeaturePage from '../components/FeaturePage';
import { Layers, Zap, Wand2 } from 'lucide-react';

const ApparelLandingPage = () => {

  // 1. NATIVE SEO TITLE UPDATE (No extra libraries needed)
  useEffect(() => {
    document.title = "AI Apparel Mockup Generator | FullMockup";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Create hyper-realistic apparel mockups using AI. Generate professional T-shirt, hoodie, and print-on-demand mockups instantly.");
    }
  }, []);

 
  
  // 1. Hero Data (Updated for Better SEO & Readability)
  const heroData = {
    // Short, Punchy Heading
    title: "Apparel Mockup Creator",
    
    // SEO-Rich Subheadline
    subtitle: "Stop using flat overlays. Our v25 Warp Engine™ maps your design to every fabric fold, creating photo-realistic mockups in seconds. No Photoshop required.",
    
    primaryBtn: "Create Free Mockup",
    primaryLink: "/app/apparel", // Directs to tool
    
    secondaryBtn: "See the Difference",
    secondaryLink: "#comparison", // Anchors to comparison section
    
    trustBadge: "Trusted by 5,000+ Etsy Sellers & Indie Brands",
    
    // IMAGE REFERENCE (For your future use):
    // Location: public/images/showcase/LandingPage/LandingPg_TShirt_with_design.webp
    image: "/images/showcase/LandingPage/LandingPg_TShirt_with_design.webp" 
  };

  // 2. Social Proof
  const socialData = {
    stats: [
      { 
        value: "Instant Render", 
        label: "Generate mockups in seconds" 
      },
      { 
        value: "4K Quality", 
        label: "High-Res Downloads" 
      },
      { 
        value: "100% Rights", 
        label: "Commercial Use License Included" 
      }
    ],
    // New Header for the logos
    title: "Optimized for major platforms:", 
    brands: ["Printful", "Printify", "Etsy", "Shopify", "WooCommerce"]
  };

  // 3. Benefits
  const benefitsData = [
    {
      icon: <Layers size={28} />,
      title: "3D Displacement Mapping",
      desc: "We don't just paste your logo. Our v25 Engine analyzes the Z-depth of the fabric, wrapping your design around wrinkles and folds for hyper-realism."
    },
    {
      icon: <Zap size={28} />,
      title: "4K Ultra-High Resolution",
      desc: "Export crisp, retina-ready images up to 4000x4000px. Perfect for zooming in on Etsy listings or printing large banners."
    },
    {
      icon: <Wand2 size={28} />,
      title: "AI Background Tools",
      desc: "Need a transparent background? Or a blurred DSLR look? Use our integrated AI tools to clean up your mockups in one click."
    }
  ];

  // 4. How It Works
  const stepsData = [
    {
      title: "Upload Your Base",
      desc: "Choose from our library of high-quality blanks or upload a photo of your own garment."
    },
    {
      title: "Drop Design",
      desc: "Upload your logo. Our v25 Engine automatically detects the chest area and warps the design to match the fabric texture."
    },
    {
      title: "Download 4K",
      desc: "Get your watermark-free, high-res PNG instantly. Ready for your store."
    }
  ];

  // 5. Comparison
  const comparisonData = {
    title: "Why FullMockup is the Smartest Choice",
    rows: [
      { feature: "Realism Strategy", us: "3D Displacement (v25)", them: "Flat Overlay (Sticker look)" },
      { feature: "Learning Curve", us: "Zero (Instant)", them: "High (Hours)" },
      { feature: "Cost", us: "Free / Affordable", them: "Expensive Subs" },
      { feature: "Export Quality", us: "4K / Lossless", them: "Often Compressed" },
      { feature: "Mobile Friendly", us: "Yes (Cloud-Based)", them: "No" }
    ]
  };

  // 6. FAQ
  const faqData = [
    {
      q: "Can I use these mockups for my Etsy or Shopify store?",
      a: "Absolutely. You own 100% of the commercial rights to every image you generate on FullMockup, even on the free plan."
    },
    {
      q: "How does the v25 Warp Engine work?",
      a: "Unlike standard overlay tools, our engine reads the light and shadow data of the clothing image to create a 'Displacement Map'. It then physically bends your 2D design to fit the 3D curves of the shirt."
    },
    {
      q: "Is it really free?",
      a: "Yes! Our Starter plan gives you free credits every single day. If you need unlimited high-res exports without watermarks, our Pro plan is just a fraction of the cost."
    },
    {
      q: "What file formats do you support?",
      a: "You can upload JPG, PNG, and WEBP files. We recommend transparent PNGs for the best results with logos."
    }
  ];

  // 7. CTA
  const ctaData = {
    title: "Ready to Sell More T-Shirts?",
    subtitle: "Join thousands of smart POD sellers creating professional assets in seconds.",
    
    // UPDATE: Changed text and link
    btnText: "Launch Creator Now", 
    link: "/app/apparel", 
    
    subText: "No credit card required for Starter plan."
  };

  // JSON-LD for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "FullMockup Apparel Mockup Generator",
    "applicationCategory": "DesignApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Create hyper-realistic apparel mockups using AI. Generate professional T-shirt, hoodie, and print-on-demand mockups instantly."
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>

      <FeaturePage 
        hero={heroData}
        socialProof={socialData}
        benefits={benefitsData}
        howItWorks={stepsData}
        comparison={comparisonData}
        faq={faqData}
        cta={ctaData}
      />
    </>
  );
};

export default ApparelLandingPage;